import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GlobalStyles } from '@mui/material';
import { TOUR_KEYFRAMES, tourPalette } from '../sections/welcome-tour/tourTheme';
import { driverPopoverStyles } from '../sections/welcome-tour/driverTheme';
import { useTheme } from '@mui/material/styles';
import { LAST_TOUR_STEP, TOUR_STEPS, TourStepDef } from '../sections/welcome-tour/tourSteps';
import { ROUTES_TOUR } from '../sections/welcome-tour/tourRoutes';
import { analytics } from 'lib/analytics';
import { markTourCompleted } from '../utils/tourFlag';
import { useUserClient } from '../client/client-hook';

// The tour runs over the real app, so its state has to outlive every route
// change and page remount — hence a provider above the router outlet plus
// sessionStorage, rather than component state.

export type TourPhase = 'tour' | 'point' | 'page';

const STORE_KEY = 'tc_welcome_tour:v1';

interface Persisted {
  active: boolean;
  step: number;
  phase: TourPhase;
  visited: Record<number, boolean>;
}

const DEFAULTS: Persisted = { active: false, step: 0, phase: 'tour', visited: {} };

const read = (): Persisted => {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
};

const write = (v: Persisted) => {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(v));
  } catch {
    /* private browsing / quota */
  }
};

interface TourContextValue {
  active: boolean;
  step: number;
  phase: TourPhase;
  visited: Record<number, boolean>;
  current: TourStepDef | undefined;
  // Menu id that stays clickable while pointing; everything else locks.
  unlockedMenuId: string | undefined;
  // Steps whose precondition holds, in original index order.
  visibleSteps: { index: number; def: TourStepDef }[];
  isVisible: (index: number) => boolean;
  start: () => void;
  goStep: (n: number) => void;
  point: () => void;
  openTarget: () => void;
  backToTour: () => void;
  completeStep: () => void;
  /** `source` attributes the exit (intro_link | topbar | appbar). */
  stop: (source?: string) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export const useTour = (): TourContextValue => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within a TourProvider');
  return ctx;
};

export const TourProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const muiTheme = useTheme();
  const getUserClient = useUserClient();
  const [s, setS] = useState<Persisted>(read);

  useEffect(() => write(s), [s]);

  // The user reaches the product page by clicking the real menu item, which
  // navigates on its own — so detect arrival rather than driving it.
  useEffect(() => {
    setS((prev) => {
      if (!prev.active || prev.phase !== 'point') return prev;
      const step = TOUR_STEPS[prev.step];
      return step?.route && pathname.startsWith(step.route) ? { ...prev, phase: 'page' } : prev;
    });
  }, [pathname]);

  const current = TOUR_STEPS[s.step];

  // No step is conditional today; the hook stays so adding one is a one-line
  // change here rather than a rewrite of goStep and the rail.
  const isVisible = useCallback((index: number) => !!TOUR_STEPS[index], []);

  const visibleSteps = useMemo(() => TOUR_STEPS.map((def, index) => ({ index, def })).filter((x) => isVisible(x.index)), [isVisible]);

  const start = useCallback(() => {
    analytics.tourStarted();
    setS({ active: true, step: 0, phase: 'tour', visited: {} });
  }, []);

  // Walk past any step whose precondition fails, in the direction of travel.
  const resolveStep = useCallback(
    (target: number, from: number) => {
      const dir = target >= from ? 1 : -1;
      let n = Math.max(0, Math.min(LAST_TOUR_STEP, target));
      while (n > 0 && n < LAST_TOUR_STEP && !isVisible(n)) n += dir;
      return Math.max(0, Math.min(LAST_TOUR_STEP, n));
    },
    [isVisible]
  );

  const goStep = useCallback(
    (n: number) => {
      // Progression telemetry. Without this the ONLY movement event was
      // tour_step_completed, which requires finishing a full product handoff —
      // so "1 step completed" read as near-total abandonment when it actually
      // measured a much rarer action. Computed outside the updater for the
      // StrictMode reason documented on completeStep.
      const next = resolveStep(n, s.step);
      if (next !== s.step) {
        analytics.tourStepViewed({
          step: next,
          key: TOUR_STEPS[next]?.key,
          from: s.step,
          direction: next > s.step ? 'forward' : 'back'
        });
      }
      setS((prev) => ({ ...prev, step: resolveStep(n, prev.step), phase: 'tour' }));
    },
    [resolveStep, s.step]
  );

  // Entering a pointing phase IS the tour running. `active` was only set by
  // start(), which auto-runs for first-time users only — so anyone who had
  // already finished the tour and opened it again got a pointing step with
  // NO highlight and no menu lock (unlockedMenuId gates on `active`).
  const point = useCallback(() => setS((prev) => ({ ...prev, active: true, phase: 'point' })), []);

  // Hand off to the real page. Navigation is real, so the overlay has to
  // re-find its anchor after the route mounts.
  const openTarget = useCallback(() => {
    setS((prev) => {
      const step = TOUR_STEPS[prev.step];
      if (step?.route) navigate(step.route);
      return { ...prev, active: true, phase: 'page' };
    });
  }, [navigate]);

  const backToTour = useCallback(() => {
    setS((prev) => ({ ...prev, phase: 'tour' }));
    navigate(ROUTES_TOUR.TOUR);
  }, [navigate]);

  const completeStep = useCallback(() => {
    // Events fire outside the setS updater — updaters can double-invoke
    // under StrictMode and must stay side-effect free.
    analytics.tourStepCompleted({ step: s.step, key: TOUR_STEPS[s.step]?.key });
    // LAST_TOUR_STEP is one past the rail — landing there means every
    // handoff step has been finished.
    if (resolveStep(s.step + 1, s.step) === LAST_TOUR_STEP) {
      analytics.tourCompleted();
      // Durable: finishing counts as done forever, across devices.
      markTourCompleted(getUserClient);
    }
    setS((prev) => ({
      ...prev,
      visited: { ...prev.visited, [prev.step]: true },
      step: resolveStep(prev.step + 1, prev.step),
      phase: 'tour'
    }));
    navigate(ROUTES_TOUR.TOUR);
  }, [navigate, resolveStep, s.step, getUserClient]);

  const stop = useCallback(
    (source?: string) => {
    // Only a skip when the tour was actually running and not already done.
    if (s.active && s.step < LAST_TOUR_STEP) {
      // `source` attributes the exit: the step-0 skips that dominate the data
      // could be the intro's own link or the top bar, and the fix differs.
      analytics.tourSkipped({ step: s.step, phase: s.phase, source: source ?? 'unknown' });
      // Skip counts as done — never re-force the tour on this user.
      markTourCompleted(getUserClient);
    }
    setS((prev) => ({ ...prev, active: false, phase: 'tour' }));
    // The dashboard is the single destination — for get-started test-arm
    // users it mounts locked; control keeps the plain dashboard.
    navigate(ROUTES_TOUR.DASHBOARD, { replace: true });
    },
    [navigate, s.active, s.step, s.phase, getUserClient]
  );

  const value = useMemo<TourContextValue>(
    () => ({
      active: s.active,
      step: s.step,
      phase: s.phase,
      visited: s.visited,
      current,
      // Only while pointing — once the user is on the page the menu stays locked
      // to that item so they can't wander mid-step.
      unlockedMenuId: s.active && (s.phase === 'point' || s.phase === 'page') ? current?.navMenuId : undefined,
      visibleSteps,
      isVisible,
      start,
      goStep,
      point,
      openTarget,
      backToTour,
      completeStep,
      stop
    }),
    [s, current, visibleSteps, isVisible, start, goStep, point, openTarget, backToTour, completeStep, stop]
  );

  return (
    <TourContext.Provider value={value}>
      {/* Keyframes live here, not on the tour page — the spotlight ring also
          animates on real product pages during a handoff. The driver.js popover
          restyle rides along for the same reason. */}
      <GlobalStyles styles={TOUR_KEYFRAMES as never} />
      <GlobalStyles styles={driverPopoverStyles(tourPalette(muiTheme.palette.mode === 'dark')) as never} />
      {children}
    </TourContext.Provider>
  );
};

export default TourProvider;
